import isEmpty from 'lodash/isEmpty';
import { ExitRules } from 'modules/journey/components/ExitRules';
import { useRuleBuilder } from 'modules/journey/components/RuleBuilder/hooks/useRuleBuilder';
import {
  CLONE_ERROR_BANNER_ID,
  CloneErrorBanner,
} from 'modules/workflowNodeEditors/components/CloneErrorBanner';
import styled from 'styled-components';
import { ErrorBanner } from 'ui/banner';
import { spacing } from 'ui/theme';

import { Spinner } from 'ui/spinner';
import { useExitRuleContext } from 'modules/workflows/context/ExitRuleContext';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { selectIsJourneySettingsRefactorFeatureFlagOn } from 'core/userContext';
import { Header, HeaderWrapper } from '../SettingsTakeover.styles';

export const SAVE_EXIT_RULES_BUTTON_TEST_ID = 'save-exit-rules-button';
export const DRAFT_EXIT_RULES_BANNER = 'draft-exit-rules-banner';
export const PUBLISHED_EXIT_RULES_BANNER = 'published-exit-rules-banner';

const StyledExitRuleBanner = styled.div`
  margin: 0 ${spacing.plusHalf} ${spacing.base};
  > div {
    margin-bottom: 0;
  }
`;

type ExitRulesProps = { goToConversionTab: () => void };

const ExitRuleBanner = () => {
  return (
    <StyledExitRuleBanner>
      <ErrorBanner data-test="exit-rules-error-banner">
        Please fix the highlighted errors.
      </ErrorBanner>
    </StyledExitRuleBanner>
  );
};

export const JourneyExitRulesOld = ({ goToConversionTab }: ExitRulesProps) => {
  const { isLoading, validationErrors, hasCloningErrors } = useRuleBuilder();
  const { loadingExitRules } = useExitRuleContext();

  return (
    <>
      <HeaderWrapper>
        <Header>Exit rules</Header>
      </HeaderWrapper>
      {hasCloningErrors && (
        <StyledExitRuleBanner data-test={CLONE_ERROR_BANNER_ID}>
          <CloneErrorBanner />
        </StyledExitRuleBanner>
      )}
      {!hasCloningErrors && !isEmpty(validationErrors) && <ExitRuleBanner />}
      <div>
        {!isLoading && !loadingExitRules ? (
          <ExitRules goToConversionTab={goToConversionTab} />
        ) : (
          <Spinner />
        )}
      </div>
    </>
  );
};

export const JourneyExitRulesNew = ({ goToConversionTab }: ExitRulesProps) => {
  const [exitRulesLoadingFirstTime, setExitRulesLoadingFirstTime] = useState(true);
  const { isLoading, validationErrors, hasCloningErrors } = useRuleBuilder();
  const { loadingExitRules } = useExitRuleContext();

  // After we have loaded the exit rule view for the fist time, we don't want to show a spinner
  useEffect(() => {
    if (exitRulesLoadingFirstTime && !loadingExitRules && !isLoading) {
      setExitRulesLoadingFirstTime(false);
    }
  }, [exitRulesLoadingFirstTime, loadingExitRules, isLoading]);

  return (
    <>
      <HeaderWrapper>
        <Header>Exit rules</Header>
      </HeaderWrapper>
      {hasCloningErrors && (
        <StyledExitRuleBanner data-test={CLONE_ERROR_BANNER_ID}>
          <CloneErrorBanner />
        </StyledExitRuleBanner>
      )}
      {!hasCloningErrors && !isEmpty(validationErrors) && <ExitRuleBanner />}
      <div>
        {!exitRulesLoadingFirstTime ? (
          <ExitRules goToConversionTab={goToConversionTab} />
        ) : (
          <Spinner />
        )}
      </div>
    </>
  );
};

export const JourneyExitRules = (props: ExitRulesProps) => {
  const isJourneySettingsFeatureFlagEnabled = useSelector(
    selectIsJourneySettingsRefactorFeatureFlagOn,
  );
  const ExitRulesFn = isJourneySettingsFeatureFlagEnabled
    ? JourneyExitRulesNew
    : JourneyExitRulesOld;
  return <ExitRulesFn {...props} />;
};
